import type { UIEvent } from "../../lib/events-adapter";
import { useEffect, useState } from "react";
import * as ApiClient from "@v0y4ge/api-client";
import { listHubs } from "@v0y4ge/api-client";
import type {
  Hub as HubDto, HubCreateInput, HubUpdateInput,
  HubFilterRule as ContractHubFilterRule, HubTheme as ContractHubTheme, HubSeo as ContractHubSeo, HubVisibility as ContractHubVisibility, HubLayout as ContractHubLayout,
} from "@v0y4ge/api-contracts";
import { studioRequestOptions } from "../../lib/studio-config";
import { logUpsert, logAction } from "../../lib/audit";
import { can, onTenantChange, onTenantSwitching } from "../../shell/access";

/**
 * Event Hubs — UI catalog + client-backed list hook.
 *
 * WIRED: `useHubs()` fetches via `listHubs()` and maps each contract `Hub` DTO →
 * the UI `Hub` at this boundary.
 *
 * REQ-014 RESOLVED (2026-07-25, Voyage pushback item 6): the full hubs family
 * ships in the typed client — `listHubs`/`getHub`/`createHub`/`updateHub`/
 * `deleteHub`/`duplicateHub`/`exportHubs`/`getPublicHub`. There is NO separate
 * publish verb by contract design (api-contracts/src/hubs.ts): publishing is
 * `updateHub({ status: 'published' })`, which sets `publishedAt`. The
 * `typeof op === "function"` guards below remain as kit-drift safety only;
 * mutations never fake success — a failed write surfaces the API's own error
 * to the caller (see HubEditor.tsx).
 */
export type HubKind = "location" | "group" | "workspace";

export interface HubFilterRule {
  kind?: HubKind;
  venue?: string; includeRule?: string; excludeRule?: string;
  tagRule?: string; autoPromote?: boolean; featuredEventId?: string;
  scope?: string; hideRule?: string;
}
/** `theme` is what the editor shows; `themeLabel` / `themeMode` are the stored
 *  theme's own label and mode, carried separately so a save never renames the
 *  label to "Light"/"Dark" (CC 0015 §1). */
export interface HubBranding { theme?: string; themeLabel?: string; themeMode?: "light" | "dark"; primaryColor?: string; logoUrl?: string; heroImageUrl?: string; }
export interface HubSeo { pageTitle?: string; metaDescription?: string; ogImageUrl?: string; }
export interface HubVisibility {
  indexed: boolean; showPastEvents: boolean; showCapacityRemaining: boolean; requireSignIn: boolean;
  /** From the wizard's public/private choice (CC 0013 §5). */
  public?: boolean;
}

/** The contract's HubFilterRule, imported rather than hand-written (CC 0030 item 22g). */
export type HubRule = ContractHubFilterRule;

export interface Hub {
  id: string; kind: HubKind; name: string; slug: string;
  description?: string | null;
  events: number; visits: number; conv: number;
  status: "published" | "draft"; edited: string; custom: string;
  // REQ-014 fields — shipped; optional/defaulted for alias tolerance.
  filterRule?: HubFilterRule;
  /** The stored canonical rules, kept verbatim so a save never drops a rule the
   *  editor cannot show (byLocationId, bySeriesId). CC 2026-09-27 item 1. */
  filterRules?: HubRule[];
  branding?: HubBranding;
  seo?: HubSeo;
  visibility?: HubVisibility;
  /** The contract's layout; the editor owns `featuredEventId` (CC 0030 item 22e). */
  layout?: ContractHubLayout;
  publicUrl?: string | null;
  publicHost?: string | null;
  publishedAt?: string | null;
}

const DEFAULT_VISIBILITY: HubVisibility = { indexed: true, showPastEvents: true, showCapacityRemaining: false, requireSignIn: false };

/** Pick events for a hub by its filter rules (interim client-side derivation
 *  — becomes redundant once REQ-014's `filterRule` is server-evaluated). */
export function eventsForHub(hub: Hub, all: UIEvent[]): UIEvent[] {
  // Department hubs (2026-08-13): a tagRule names the department tag every
  // seed event carries (tags[0]) — membership derives from the events seed
  // rather than a duplicated id list, so reseeding events re-populates hubs.
  const tagRule = hub.filterRule?.tagRule;
  if (tagRule) return all.filter((e) => (e.tags || []).includes(tagRule));
  // The strict HubListResponse parse strips HubDetail-only fields (filterRules)
  // from list rows, so department hubs are authored with slug === tag and the
  // slug is the membership key that survives the wire. Claims the hub only when
  // the tag matches real events — legacy demo hubs fall through to the
  // heuristics below.
  if (hub.kind === "group") {
    const bySlug = all.filter((e) => (e.tags || []).includes(hub.slug));
    if (bySlug.length) return bySlug;
  }
  if (hub.kind === "workspace") return all;
  if (hub.kind === "location") {
    const key = hub.name.split(" · ")[0];
    return all.filter((e) => e.location.includes(key));
  }
  if (hub.slug === "memory-bank-2026") return all.filter((e) => /memory bank/i.test(e.title));
  if (hub.slug === "wealth-uk") return all.filter((e) => /wealth|mortgage|first-time/i.test(e.title));
  if (hub.slug === "tech-workshops") return all.filter((e) => /workshop|tech|onboarding/i.test(e.title));
  return all.slice(0, 4);
}

export const KIND_LABEL: Record<HubKind, string> = { location: "Location", group: "Series", workspace: "Workspace" };
export const KIND_TONE: Record<HubKind, "info" | "primary" | "success"> = { location: "info", group: "primary", workspace: "success" };

/** Contract Hub DTO → UI Hub (superset; alias-tolerant; REQ-014 fields optional). */
function hubFromDto(d: HubDto): Hub {
  const dd = d as HubDto & {
    eventsCount?: number; eventCount?: number; visits30d?: number; conversions?: number;
    filterRule?: HubFilterRule; branding?: HubBranding; seo?: HubSeo; visibility?: Partial<HubVisibility>;
    publicUrl?: string | null; publicHost?: string | null; publishedAt?: string | null;
    /** Contract HubDetail.filterRules — the canonical rule list (2026-08-13). */
    filterRules?: { kind: string; tag?: string; locationId?: string; seriesId?: string }[];
  };
  // Canonical contract rule → UI filterRule. byTag is the department-hub shape;
  // the legacy loose filterRule wins only when no canonical rules are present.
  const byTag = dd.filterRules?.find((r) => r.kind === "byTag");
  return {
    id: d.id,
    kind: d.kind as HubKind,
    name: d.name,
    slug: d.slug,
    events: d.events ?? dd.eventsCount ?? dd.eventCount ?? 0,
    visits: d.visits ?? dd.visits30d ?? 0,
    conv: d.conv ?? dd.conversions ?? 0,
    status: d.status === "draft" ? "draft" : "published",
    edited: d.edited || "—",
    custom: d.custom || "—",
    filterRule: byTag ? { kind: d.kind as HubKind, tagRule: byTag.tag } : (dd.filterRule ?? { kind: d.kind as HubKind }),
    filterRules: Array.isArray(dd.filterRules) ? (dd.filterRules as HubRule[]) : undefined,
    /* The server sends `theme`, not `branding`; map it back onto the editor's
       fields so a saved hub reads what it stored. */
    branding: (() => { const th = (dd as { theme?: { label?: string; primaryColor?: string; mode?: string; logoAsset?: string; heroImageAsset?: string } }).theme;
      if (th) return { theme: th.mode ? (th.mode === "dark" ? "Dark" : "Light") : th.label, themeLabel: th.label, themeMode: th.mode === "dark" || th.mode === "light" ? th.mode : undefined, primaryColor: th.primaryColor, logoUrl: th.logoAsset, heroImageUrl: th.heroImageAsset };
      return dd.branding ?? {}; })(),
    seo: (() => { const x = dd.seo as { pageTitle?: string; metaDescription?: string; ogImage?: string; ogImageUrl?: string } | undefined;
      return x ? { pageTitle: x.pageTitle, metaDescription: x.metaDescription, ogImageUrl: x.ogImage ?? x.ogImageUrl } : {}; })(),
    visibility: (() => { const v = (dd.visibility ?? {}) as Partial<HubVisibility> & { requireSignInToRegister?: boolean; public?: boolean };
      return { ...DEFAULT_VISIBILITY, ...v, requireSignIn: v.requireSignInToRegister ?? v.requireSignIn ?? DEFAULT_VISIBILITY.requireSignIn, ...(v.public !== undefined ? { public: v.public } : {}) } as HubVisibility; })(),
    layout: (dd as { layout?: ContractHubLayout }).layout,
    publicUrl: dd.publicUrl ?? null,
    publicHost: dd.publicHost ?? null,
    publishedAt: dd.publishedAt ?? null,
  };
}

// Module store (parity with landing/templates) so a hub mutated from the list
// reflects immediately and the list can refetch after an op.
let _hubs: Hub[] | null = null;
let _hubsStarted = false;
/** Why the last listHubs read failed (null = it did not). A refused read is not an empty list (CC, 2026-10-05). */
let _hubsError: string | null = null;
const _hubSubs = new Set<() => void>();
const _hubEmit = () => _hubSubs.forEach((f) => { try { f(); } catch { /* noop */ } });
onTenantSwitching(() => { _hubs = null; _hubsStarted = false; _hubsError = null; _hubEmit(); });
onTenantChange(() => { if (can("locations.read")) void _loadHubs(true); }); // admin/hubs — gated (docket 2026-09-17); hubs sit under the Resources/locations rail cap
async function _loadHubs(force = false): Promise<void> {
  if (_hubsStarted && !force) return;
  _hubsStarted = true;
  try {
    if (typeof listHubs !== "function") { _hubs = _hubs ?? []; _hubEmit(); return; }
    const r = await listHubs({}, studioRequestOptions("hubs"));
    const arr = (r as { hubs?: HubDto[] })?.hubs;
    _hubs = Array.isArray(arr) ? arr.map(hubFromDto) : [];
    _hubsError = null;
    _hubEmit();
  } catch (e) { _hubs = _hubs ?? []; _hubsError = (e as { message?: string } | null)?.message || "Request failed"; _hubEmit(); } // CC, 2026-10-05: record the refusal instead of passing it off as an empty list
}
/** Re-run the hubs fetch (post-mutation). */
export function refetchHubs(): void { void _loadHubs(true); }
/** Remove a hub from the store (post-delete reflect). */
export function removeHubLocal(id: string): void { _hubs = (_hubs ?? []).filter((h) => h.id !== id); _hubEmit(); }
/** Upsert a hub DTO into the store (post duplicate/publish reflect). */
export function reflectHubDto(dto: HubDto): Hub {
  const h = hubFromDto(dto);
  const cur = _hubs ?? [];
  const i = cur.findIndex((x) => x.id === h.id);
  _hubs = i >= 0 ? cur.map((x) => (x.id === h.id ? h : x)) : [h, ...cur];
  _hubEmit();
  return h;
}

/** `enabled: false` (a role without hubs.read) skips the read; the default keeps every caller as it was. */
export function useHubs(enabled = true): Hub[] {
  const [, force] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const cb = () => force((n) => n + 1);
    _hubSubs.add(cb);
    void _loadHubs();
    return () => { _hubSubs.delete(cb); };
  }, [enabled]);
  return _hubs ?? [];
}

/** The last listHubs failure, or null. Lets a screen tell "refused" from "no hubs" (CC, 2026-10-05). */
export function useHubsLoadError(): string | null {
  const [, force] = useState(0);
  useEffect(() => {
    const cb = () => force((n) => n + 1);
    _hubSubs.add(cb);
    return () => { _hubSubs.delete(cb); };
  }, []);
  return _hubsError;
}

type AnyFn = (...a: unknown[]) => Promise<unknown>;
const _op = (name: string): AnyFn | null => {
  const fn = (ApiClient as unknown as Record<string, unknown>)[name];
  return typeof fn === "function" ? (fn as AnyFn) : null;
};

function _hubDto(r: unknown): HubDto | null {
  const d = (r as { hub?: HubDto; data?: HubDto })?.hub ?? (r as { data?: HubDto })?.data ?? (r as HubDto);
  return d && (d as HubDto).id ? d as HubDto : null;
}
/** Duplicate a hub (`duplicateHub`, shipped). */
export async function duplicateHubOp(id: string): Promise<{ ok: true; hub: Hub } | { ok: false }> {
  const op = _op("duplicateHub");
  if (!op) return { ok: false };
  const dto = _hubDto(await op(id, studioRequestOptions("hubs")));
  return dto ? { ok: true, hub: reflectHubDto(dto) } : { ok: false };
}
/** Delete a hub (`deleteHub`). */
export async function deleteHubOp(id: string): Promise<{ ok: boolean }> {
  const op = _op("deleteHub");
  if (!op) return { ok: false };
  await op(id, studioRequestOptions("hubs"));
  removeHubLocal(id);
  return { ok: true };
}
/** Publish / unpublish a hub — no distinct verb by contract design;
 *  `updateHub({status})` drives it and sets `publishedAt`. */
export async function setHubPublish(id: string, status: "published" | "draft"): Promise<{ ok: true; hub: Hub } | { ok: false }> {
  const op = _op("updateHub");
  if (!op) return { ok: false };
  const dto = _hubDto(await op(id, { status }, studioRequestOptions("hubs")));
  return dto ? { ok: true, hub: reflectHubDto(dto) } : { ok: false };
}
/** Export hubs CSV (`exportHubs`). Returns a download URL when available. */
export async function exportHubsOp(): Promise<{ ok: true; downloadUrl?: string; filename?: string } | { ok: false }> {
  const op = _op("exportHubs");
  if (!op) return { ok: false };
  const r = await op({ format: "csv", scope: "filtered" }, studioRequestOptions("hubs")) as { downloadUrl?: string; filename?: string };
  return { ok: true, downloadUrl: r?.downloadUrl, filename: r?.filename };
}

/** Single-hub read for the editor (`getHub`, shipped). Falls back to finding the
 *  hub in the already-fetched list if the op is ever absent. */
/** Read one hub, saying whether the answer came from the DETAIL read (it
 *  carries the stored filterRules) or the list fallback (it does not). Only a
 *  detail answer may seed a save's baseline (CC 0018 §1.2). */
export async function getHubDetail(id: string, listFallback: Hub[]): Promise<{ hub: Hub; detail: boolean } | null> {
  const op = _op("getHub");
  if (op) {
    try {
      const r = await op(id, studioRequestOptions("hubs")) as { hub?: HubDto } | HubDto;
      const dto = (r as { hub?: HubDto })?.hub ?? (r as HubDto);
      if (dto?.id) return { hub: hubFromDto(dto), detail: true };
    } catch (e) { console.warn("getHub failed:", e); }
  }
  const row = listFallback.find((h) => h.id === id);
  return row ? { hub: row, detail: false } : null;
}

export async function getHub(id: string, listFallback: Hub[]): Promise<Hub | null> {
  return (await getHubDetail(id, listFallback))?.hub ?? null;
}

/** Save a hub's draft (create if new, update if existing). Returns
 *  `{ ok:false }` only if the op is absent from the kit (drift guard) —
 *  a rejected API write THROWS to the caller, which surfaces the real error. */
/** The one valid rule for a hub the editor is creating, from what it knows. */
function ruleFor(hub: Hub): HubRule[] | null {
  const tag = (hub.filterRule as { tagRule?: string } | undefined)?.tagRule;
  if (tag) return [{ kind: "byTag", tag }];
  if (hub.kind === "workspace") return [{ kind: "workspace" }];
  /* The stored rule's location first (CC 0015 §1.1): the editor never sets
     filterRule.locationId, so reading only that dropped a location hub's rule. */
  const storedLoc = hub.filterRules?.find((x): x is Extract<HubRule, { kind: "byLocationId" }> => x.kind === "byLocationId");
  const loc = (hub.filterRule as { locationId?: string } | undefined)?.locationId ?? storedLoc?.locationId;
  if (hub.kind === "location" && loc) return [{ kind: "byLocationId", locationId: loc }];
  const series = (hub.filterRule as { seriesId?: string } | undefined)?.seriesId;
  if (series) return [{ kind: "bySeriesId", seriesId: series }];
  /* A group hub (a Series hub's kind is "group") with no explicit rule: the slug IS the tag. That is
     the membership key eventsForHub already uses for department hubs, and the
     wizard never collects a tag (CC 2026-09-27, item 1). */
  if (hub.kind === "group" && hub.slug) return [{ kind: "byTag", tag: hub.slug }];
  return null;
}

/** Why a hub cannot be created, in words, when ruleFor finds nothing. */
export function noRuleReason(hub: Hub): string {
  return hub.kind === "location"
    ? "Choose the location this hub lists."
    : "This hub needs a rule for which events it lists.";
}

/* ── theme / seo / visibility on the CONTRACT's shapes (CC item 2) ──────────
   HubTheme { label, primaryColor, mode?, logoAsset?, heroImageAsset? },
   HubSeo { pageTitle, metaDescription, ogImage? }, HubVisibility's five
   required booleans. The editor's shapes differ; these map it across and
   return null when the result would not be whole and valid, so the field is
   left out rather than refused. */
type ContractTheme = ContractHubTheme;
type ContractSeo = ContractHubSeo;
type ContractVis = ContractHubVisibility;
function themeOut(b?: HubBranding): ContractTheme | null {
  if (!b || !b.primaryColor) return null;
  /* Label and mode travel separately (CC 0015 §1). The stored label stands
     until the editor's Theme field is actually changed. */
  const shown = b.themeMode ? (b.themeMode === "dark" ? "Dark" : "Light") : (b.themeLabel || "");
  const edited = (b.theme || "") !== shown;
  const typed = (b.theme || "").trim();
  const m = typed.toLowerCase();
  const label = edited ? (typed || "Default") : (b.themeLabel || typed || "Default");
  const mode = edited ? (m === "light" || m === "dark" ? m : b.themeMode) : b.themeMode;
  return { label, primaryColor: b.primaryColor,
    ...(mode ? { mode } : {}),
    ...(b.logoUrl ? { logoAsset: b.logoUrl } : {}),
    ...(b.heroImageUrl ? { heroImageAsset: b.heroImageUrl } : {}) };
}
function seoOut(x?: HubSeo): ContractSeo | null {
  if (!x || !x.pageTitle || !x.metaDescription) return null;
  return { pageTitle: x.pageTitle, metaDescription: x.metaDescription, ...(x.ogImageUrl ? { ogImage: x.ogImageUrl } : {}) };
}
function visOut(v?: HubVisibility, status?: string): ContractVis | null {
  if (!v) return null;
  return { public: (v as { public?: boolean }).public ?? status !== "draft", indexed: !!v.indexed, showPastEvents: !!v.showPastEvents,
    showCapacityRemaining: !!v.showCapacityRemaining, requireSignInToRegister: !!v.requireSignIn };
}
const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/* SAVE BUILDS THE CONTRACT BODY, NOTHING ELSE (CC 2026-09-27 item 1).
   HubCreateInput / HubUpdateInput are strict: spreading the UI hub sent id,
   counts, branding and publicUrl and was refused. `[{ kind: hub.kind }]` was
   never a rule, and the echoed rule broke the response parse after the write
   had committed — "Couldn't save" on a saved hub, a duplicate on retry. */
export async function saveHub(hub: Hub, isNew: boolean, prev?: Hub): Promise<{ ok: true; hub: Hub } | { ok: false; reason?: string }> {
  const op = _op(isNew ? "createHub" : "updateHub");
  if (!op) return { ok: false };
  const h = hub as Hub & { customDomain?: string; globalCss?: string };
  const body: Partial<HubCreateInput> & HubUpdateInput = {
    kind: h.kind, name: h.name, slug: h.slug, status: h.status,
    ...(h.description !== undefined ? { description: h.description } : {}),
    ...(h.custom && h.custom !== "—" ? { custom: h.custom } : {}),
    ...(h.customDomain !== undefined ? { customDomain: h.customDomain } : {}),
    ...(h.layout !== undefined && (isNew || !prev || !same(prev.layout, h.layout)) ? { layout: h.layout as ContractHubLayout } : {}),
    ...(h.globalCss !== undefined ? { globalCss: h.globalCss } : {}),
  };
  /* Each sent only when it changed (create: when present) AND maps to a whole,
     valid contract shape; otherwise left out, and the server keeps what it has. */
  const th = themeOut(hub.branding), se = seoOut(hub.seo), vi = visOut(hub.visibility, hub.status);
  /* A half-filled SEO edit cannot be sent (the contract needs both), and it must
     not vanish behind a success toast (CC 0015 §1). */
  const seoTouched = !!hub.seo && !!(hub.seo.pageTitle || hub.seo.metaDescription) && (isNew || !prev || !same(prev.seo, hub.seo));
  if (!se && seoTouched) return { ok: false, reason: "The page title and the meta description are both needed to save the search settings." };
  if (th && (isNew || !prev || !same(themeOut(prev.branding), th))) body.theme = th;
  if (se && (isNew || !prev || !same(seoOut(prev.seo), se))) body.seo = se;
  if (vi && (isNew || !prev || !same(visOut(prev.visibility, prev.status), vi))) body.visibility = vi;
  if (isNew) {
    const rules = ruleFor(hub);
    if (!rules) return { ok: false, reason: noRuleReason(hub) };
    body.filterRules = rules;
  } else {
    /* Only when the TAG changed (CC 0015 §1.2). Comparing the whole filterRule
       object treated a first edit as a change, and rebuilt rules from what the
       editor can see — a series rule and a stored rule's label and
       includeDrafts were lost. The tag is the only rule field the editor edits.
       On a real change, the stored rule's other fields are carried across. */
    const tagOf = (x?: Hub) => (x?.filterRule as { tagRule?: string } | undefined)?.tagRule ?? "";
    if (prev && tagOf(prev) !== tagOf(hub) && tagOf(hub)) {
      /* The server REPLACES filterRules, so build from the stored list and swap
         only the byTag entry the editor owns; every other rule is kept verbatim. */
      /* Only with the stored list actually read (CC 0018 §1.1): a partial list
         would REPLACE the hub's rules. No list, no rule write. */
      if (!Array.isArray(prev.filterRules)) return { ok: false, reason: "This hub's rules haven't loaded, so the tag change wasn't saved. Reopen the hub and try again." };
      const stored = prev.filterRules as HubRule[];
      /* The public hub reads the FIRST rule. On a hub led by a series or
         workspace rule an appended tag would change nothing public, so it is
         refused rather than reported as saved (CC 0018 §2.4). */
      if (stored[0] && (stored[0].kind === "bySeriesId" || stored[0].kind === "workspace")) return { ok: false, reason: "This hub lists events by series or by workspace, not by tag, so a tag can't be set here." };
      const old = stored.find((x) => x.kind === "byTag") as Extract<HubRule, { kind: "byTag" }> | undefined;
      const next: HubRule = { kind: "byTag", tag: tagOf(hub), ...(old?.label ? { label: old.label } : {}), ...(old?.includeDrafts !== undefined ? { includeDrafts: old.includeDrafts } : {}) };
      body.filterRules = old ? stored.map((x) => (x === old ? next : x)) : [...stored, next];
    }
  }
  const r = (isNew
    ? await op(body, studioRequestOptions("hubs"))
    : await op(hub.id, body, studioRequestOptions("hubs"))) as { hub?: HubDto } | HubDto;
  const dto = (r as { hub?: HubDto })?.hub ?? (r as HubDto);
  const saved = dto?.id ? hubFromDto(dto) : hub;
  logUpsert("hub", isNew ? null : (hub as unknown as Record<string, unknown>), saved as unknown as Record<string, unknown>, { name: (saved as { name?: string }).name, action: isNew ? "created" : "updated" });
  return { ok: true, hub: saved };
}

/** Publish a hub (goes live). Contract has no `publishHub` verb — this is
 *  `updateHub({ status: 'published' })` (UIR-23: the old `_op("publishHub")`
 *  lookup resolved an op that never existed, so publish always "failed"). */
export async function publishHubOp(id: string): Promise<{ ok: true; hub: Hub } | { ok: false }> {
  const r = await setHubPublish(id, "published");
  if (r.ok) logAction("hub", r.hub.id, (r.hub as { name?: string }).name || r.hub.id, "published");
  return r;
}
